import { lazy, Suspense, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AppLayout } from '@components/layout/AppLayout';
import { ProtectedRoute } from '@components/routes/ProtectedRoute';
import { PublicOnlyRoute } from '@components/routes/PublicOnlyRoute';
import { useAuthBootstrap } from '@features/auth';
import { useAuthStore } from '@store/auth';
import { CookieConsentBanner } from '@components/CookieConsentBanner';
import { CanonicalUpdater } from '@components/CanonicalUpdater';

// Auth sayfaları — küçük, mevcut bundle'da kalsın (lazy etmiyoruz, login ilk açılış)
import { LoginPage } from '@features/auth';
import { RegisterPage } from '@features/auth';
import { ForgotPasswordPage } from '@features/auth';
import { ResetPasswordPage } from '@features/auth';
import { KvkkAydinlatmaPage } from '@features/auth';
import { AuthCallback } from '@features/auth';
import { ConnectMcpPage } from '@features/auth';

// Korumalı sayfalar — lazy load, sadece auth'tan sonra ihtiyaç oldukça yüklenir
// Bu, ilk yüklemede ~500KB JS tasarrufu sağlar (kullanıcı login olmadan
// dashboard, recharts, dnd-kit, xlsx vs. indirilmesin).
const DashboardPage = lazy(() =>
  import('@features/dashboard/DashboardPage').then((m) => ({ default: m.DashboardPage })),
);
const MusteriListPage = lazy(() =>
  import('@features/musteri/MusteriListPage').then((m) => ({ default: m.MusteriListPage })),
);
const MusteriDetailPage = lazy(() =>
  import('@features/musteri/MusteriDetailPage').then((m) => ({ default: m.MusteriDetailPage })),
);
const AktivitePage = lazy(() =>
  import('@features/aktivite/AktivitePage').then((m) => ({ default: m.AktivitePage })),
);
const FirsatKanbanPage = lazy(() =>
  import('@features/firsat/FirsatKanbanPage').then((m) => ({ default: m.FirsatKanbanPage })),
);
const FirsatDetailPage = lazy(() =>
  import('@features/firsat/FirsatDetailPage').then((m) => ({ default: m.FirsatDetailPage })),
);
const TakvimPage = lazy(() =>
  import('@features/takvim/TakvimPage').then((m) => ({ default: m.TakvimPage })),
);
const AyarlarPage = lazy(() =>
  import('@features/ayarlar/AyarlarPage').then((m) => ({ default: m.AyarlarPage })),
);
const UrunListPage = lazy(() =>
  import('@features/urun/UrunListPage').then((m) => ({ default: m.UrunListPage })),
);
const TeklifListPage = lazy(() =>
  import('@features/teklif/TeklifListPage').then((m) => ({ default: m.TeklifListPage })),
);
const TeklifDetailPage = lazy(() =>
  import('@features/teklif/TeklifDetailPage').then((m) => ({ default: m.TeklifDetailPage })),
);
const SiparisListPage = lazy(() =>
  import('@features/siparis/SiparisListPage').then((m) => ({ default: m.SiparisListPage })),
);
const SiparisDetailPage = lazy(() =>
  import('@features/siparis/SiparisDetailPage').then((m) => ({ default: m.SiparisDetailPage })),
);
const TalepListPage = lazy(() =>
  import('@features/destek/TalepListPage').then((m) => ({ default: m.TalepListPage })),
);
const TalepDetailPage = lazy(() =>
  import('@features/destek/TalepDetailPage').then((m) => ({ default: m.TalepDetailPage })),
);
const EpostaListPage = lazy(() =>
  import('@features/eposta/EpostaListPage').then((m) => ({ default: m.EpostaListPage })),
);
const EpostaDetailPage = lazy(() =>
  import('@features/eposta/EpostaDetailPage').then((m) => ({ default: m.EpostaDetailPage })),
);
const MetaYonetimPage = lazy(() =>
  import('@features/meta/MetaYonetimPage').then((m) => ({ default: m.MetaYonetimPage })),
);
const PortalPage = lazy(() =>
  import('@features/portal/PortalPage').then((m) => ({ default: m.PortalPage })),
);
const AiSablonlarPage = lazy(() =>
  import('@features/ai/AiSablonlarPage').then((m) => ({ default: m.AiSablonlarPage })),
);
const EgitimPage = lazy(() =>
  import('@features/egitim/EgitimPage').then((m) => ({ default: m.EgitimPage })),
);
const AiMaliyetPage = lazy(() =>
  import('@features/ai/AiMaliyetPage').then((m) => ({ default: m.AiMaliyetPage })),
);

/** Suspense fallback — chunk yüklenirken görünür.
 *  250ms'den hızlı yüklenirse hiç render edilmez (flash önler).
 *  Cache'den gelen chunk'lar tipik <50ms; bu sayede "Yukleniyor..." metnini
 *  kullanici hic gormez. Yavas baglantida 250ms sonra fade-in olur. */
function RouteFallback() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setShow(true), 250);
    return () => clearTimeout(t);
  }, []);
  if (!show) return null;
  return (
    <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
      <div className="animate-pulse">Yükleniyor…</div>
    </div>
  );
}

/**
 * Idle prefetch: kullanıcı login olduktan sonra boş zamanda en sık kullanılan
 * route chunk'larını arka planda indir. Sonuç: menüden sayfaya geçiş ANINDA
 * olur (chunk zaten cache'de). İlk paint'i bloklamaz (requestIdleCallback).
 */
function prefetchHotRoutes() {
  void import('@features/dashboard/DashboardPage');
  void import('@features/musteri/MusteriListPage');
  void import('@features/firsat/FirsatKanbanPage');
  void import('@features/aktivite/AktivitePage');
  void import('@features/takvim/TakvimPage');
  void import('@features/urun/UrunListPage');
  void import('@features/teklif/TeklifListPage');
  void import('@features/siparis/SiparisListPage');
  void import('@features/destek/TalepListPage');
  void import('@features/eposta/EpostaListPage');
  void import('@features/ayarlar/AyarlarPage');
}

function useRoutePrefetch() {
  const session = useAuthStore((s) => s.session);
  useEffect(() => {
    if (!session) return;
    // Login olur olmaz hemen indir — idle beklemiyoruz, kullanici her an
    // menuye tiklayabilir. Bu chunk'lar paralel iner, ana thread'i
    // bloklamaz (browser network kuyrugu).
    const t = setTimeout(prefetchHotRoutes, 100);
    return () => clearTimeout(t);
  }, [session]);
}

export function App() {
  useAuthBootstrap(); // Supabase session'ı yükle + dinle
  useRoutePrefetch(); // Login sonrası idle'da route chunk'larını prefetch et

  return (
    <BrowserRouter>
      <CanonicalUpdater />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* Tamamen public — auth durumundan bağımsız */}
          <Route path="/kvkk" element={<KvkkAydinlatmaPage />} />
          <Route path="/portal/:slug" element={<PortalPage />} />
          {/* Reset-password recovery session ile açıldığı için PublicOnly
              dışında — yoksa otomatik /dashboard'a redirect olur. */}
          <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
          {/* Google OAuth callback — code'u session'a takas eder. Auth
              guard'sız: henüz session yokken çalışması gerekir. */}
          <Route path="/auth/callback" element={<AuthCallback />} />
          {/* MCP bağlantı köprüsü — Claude connector buraya yönlenir.
              Auth durumundan bağımsız: girişliyse handoff'u tamamlar,
              değilse login'e gönderir. */}
          <Route path="/baglan" element={<ConnectMcpPage />} />

          {/* Public — sadece logged-out */}
          <Route element={<PublicOnlyRoute />}>
            <Route path="/auth/login" element={<LoginPage />} />
            <Route path="/auth/register" element={<RegisterPage />} />
            <Route
              path="/auth/forgot-password"
              element={<ForgotPasswordPage />}
            />
          </Route>

          {/* Protected — auth gerekir */}
          <Route element={<ProtectedRoute />}>
            <Route element={<AppLayout />}>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/musteriler" element={<MusteriListPage />} />
              <Route path="/musteriler/:id" element={<MusteriDetailPage />} />
              <Route path="/aktiviteler" element={<AktivitePage />} />
              <Route path="/firsatlar" element={<FirsatKanbanPage />} />
              <Route path="/firsatlar/:id" element={<FirsatDetailPage />} />
              <Route path="/takvim" element={<TakvimPage />} />
              <Route path="/urunler" element={<UrunListPage />} />
              <Route path="/teklifler" element={<TeklifListPage />} />
              <Route path="/teklifler/:id" element={<TeklifDetailPage />} />
              <Route path="/siparisler" element={<SiparisListPage />} />
              <Route path="/siparisler/:id" element={<SiparisDetailPage />} />
              <Route path="/talepler" element={<TalepListPage />} />
              <Route path="/talepler/:id" element={<TalepDetailPage />} />
              <Route path="/epostalar" element={<EpostaListPage />} />
              <Route path="/epostalar/:id" element={<EpostaDetailPage />} />
              <Route path="/meta-yonetim" element={<MetaYonetimPage />} />
              <Route path="/ayarlar" element={<AyarlarPage />} />
              <Route path="/egitim" element={<EgitimPage />} />
              <Route path="/ai-sablonlar" element={<AiSablonlarPage />} />
              <Route path="/admin/ai-maliyet" element={<AiMaliyetPage />} />
            </Route>
          </Route>

          {/* 404 fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
      <CookieConsentBanner />
    </BrowserRouter>
  );
}
